import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Settings2, Crown, Globe, Flame, Users, GraduationCap, ChevronDown } from "lucide-react";
import { useI18n } from "../../lib/i18n";
import { LOGO } from "./SiteJsonLd";

const navItem = ({ isActive }) =>
  `px-3 py-2 text-sm tracking-wide uppercase whitespace-nowrap transition-colors ${
    isActive ? "text-white" : "text-[color:var(--text-muted)] hover:text-white"
  }`;

const LiveDot = () => (
  <span className="relative inline-flex w-1.5 h-1.5">
    <span className="absolute inset-0 rounded-full bg-red-500 animate-ping" />
    <span className="relative rounded-full bg-red-500 w-1.5 h-1.5" />
  </span>
);

const IconLabel = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5"><Icon className="w-3.5 h-3.5 text-[color:var(--accent)]" />{children}</span>
);

const MoreMenu = ({ t }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const item = ({ isActive }) => `block px-4 py-2 text-sm uppercase tracking-wide whitespace-nowrap transition-colors ${isActive ? "text-white" : "text-[color:var(--text-muted)] hover:text-white hover:bg-white/5"}`;
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(o => !o)} data-testid="nav-more" aria-expanded={open} className="px-3 py-2 text-sm tracking-wide uppercase whitespace-nowrap text-[color:var(--text-muted)] hover:text-white inline-flex items-center gap-1 transition-colors">
        Plus <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 min-w-[200px] glass border border-[color:var(--border)] rounded-xl py-2 shadow-xl z-50" data-testid="nav-more-menu" onClick={() => setOpen(false)}>
          <NavLink to="/party" className={item} data-testid="nav-party"><IconLabel icon={Users}>Watch Party</IconLabel></NavLink>
          <NavLink to="/search" className={item} data-testid="nav-search">{t("nav.search")}</NavLink>
          <NavLink to="/blog" className={item} data-testid="nav-blog">Blog</NavLink>
          <NavLink to="/partners" className={item} data-testid="nav-partners">{t("nav.partners")}</NavLink>
          <NavLink to="/about" className={item} data-testid="nav-about">{t("nav.about")}</NavLink>
        </div>
      )}
    </div>
  );
};

const MainNav = ({ t }) => (
  <nav className="hidden md:flex items-center gap-0.5 ml-2">
    <NavLink to="/" end className={navItem} data-testid="nav-home">{t("nav.home")}</NavLink>
    <NavLink to="/channels" className={navItem} data-testid="nav-channels">{t("nav.channels")}</NavLink>
    <NavLink to="/live" className={navItem} data-testid="nav-live"><span className="inline-flex items-center gap-1.5"><LiveDot />Direct</span></NavLink>
    <NavLink to="/guide" className={navItem} data-testid="nav-guide">Programme</NavLink>
    <NavLink to="/trending" className={navItem} data-testid="nav-trending"><IconLabel icon={Flame}>Tendances</IconLabel></NavLink>
    <NavLink to="/cours" className={navItem} data-testid="nav-courses"><IconLabel icon={GraduationCap}>Cours</IconLabel></NavLink>
    <NavLink to="/membership" className={navItem} data-testid="nav-membership"><IconLabel icon={Crown}>{t("nav.membership")}</IconLabel></NavLink>
    <MoreMenu t={t} />
  </nav>
);

const SearchForm = ({ t }) => {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };
  return (
    <form onSubmit={submit} className="ml-auto flex-1 max-w-md relative hidden sm:block">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t("search.placeholder")}
        className="w-full bg-[color:var(--bg-elev)] border border-[color:var(--border)] rounded-full pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-[color:var(--accent)] transition-colors"
      />
    </form>
  );
};

export const Header = () => {
  const { t, lang, setLang } = useI18n();
  return (
    <header className="sticky top-0 z-40 glass border-b border-[color:var(--border)]">
      <div className="max-w-[1400px] mx-auto flex items-center gap-4 px-6 py-3">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-link">
          <img src={LOGO} alt="Fedevo" className="h-10 w-auto" />
          <span className="font-display text-2xl tracking-wider hidden sm:inline whitespace-nowrap">
            FEDEVO<span className="text-[color:var(--accent)]"> 2.0</span>
            <span className="ml-2 text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-muted)] hidden xl:inline">Urban Media</span>
          </span>
        </Link>
        <MainNav t={t} />
        <SearchForm t={t} />
        <button
          data-testid="lang-toggle"
          onClick={() => setLang(lang === "fr" ? "en" : "fr")}
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[color:var(--text-muted)] hover:text-[color:var(--accent)] transition-colors px-2 py-1 border border-[color:var(--border)] rounded-full"
          title={lang === "fr" ? "Switch to English" : "Passer en français"}
        >
          <Globe className="w-3.5 h-3.5" /> {lang.toUpperCase()}
        </button>
        <Link to="/admin" className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[color:var(--text-muted)] hover:text-white transition-colors" data-testid="nav-admin">
          <Settings2 className="w-3.5 h-3.5" /> {t("nav.admin")}
        </Link>
      </div>
    </header>
  );
};
